import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { graphKeyLabel, graphHopsLabel, graphOutcome } from 'DataGraph/dataGraphApi';
import { graphPath } from 'DataGraph/paths';
import { engineBadge, engineLabel, fmtWhen } from '../tdmUtils';
import SectionTitle from '../SectionTitle';

/** The data graph a source row points at, from the graphs listed for its data source. */
export const chosenGraph = (m, graphsBySource) => ((graphsBySource && graphsBySource[String(m.dataSourceId)]) || [])
  .find((g) => String(g.id) === String(m.dataGraphId)) || null;

/**
 * Why a source cannot be saved as it stands, or null: no data graph exists for it ("not
 * configured"), none is picked, the parent's graph has no key table (row selection starts
 * there), or — with two or more sources — its graph has no key, or the parent's graph names no
 * key (the subset's shared key name). Mirrors the platform's 400s.
 */
export const sourceGraphProblem = (m, graphsBySource, sourceCount) => {
  const options = (graphsBySource && graphsBySource[String(m.dataSourceId)]) || [];
  if (options.length === 0) return 'NOT_CONFIGURED';
  const g = chosenGraph(m, graphsBySource);
  if (!g) return 'NOT_PICKED';
  if (m.parent && !g.keyTable) return 'PARENT_NO_KEY';
  if (sourceCount > 1 && !(g.keyTable && g.keyColumn)) return 'NO_KEY';
  if (sourceCount > 1 && m.parent && !g.keyName) return 'NO_KEY_NAME';
  return null;
};

/**
 * What a LIST graph's last Test says (3.1; `g.lastTest` = { state, finishedAt, errors, warnings } or null): a
 * graph tested since it last changed, with how many errors / warnings its report holds; one never tested
 * since (every edit clears the test); one whose last Test failed. Informs, never blocks: the run goes ahead.
 */
export const graphTestLine = (g) => {
  if (!g || graphOutcome(g) !== 'LIST') return null;
  const t = g.lastTest;
  if (!t) return { tone: 'warn', text: 'Not tested since it changed: the Test shows what each object’s rules reach.' };
  if (t.state === 'RUNNING') return { tone: 'muted', text: 'Test running…' };
  if (t.state === 'FAILED') return { tone: 'warn', text: 'Its last Test failed.' };
  const parts = [];
  if (t.errors) parts.push(`${t.errors} error${t.errors === 1 ? '' : 's'}`);
  if (t.warnings) parts.push(`${t.warnings} warning${t.warnings === 1 ? '' : 's'}`);
  return { tone: t.errors ? 'warn' : 'muted', text: `Tested ${fmtWhen(t.finishedAt)}${parts.length ? ` · ${parts.join(', ')}` : ''}.` };
};

const PROBLEM_TEXT = {
  NOT_CONFIGURED: 'Not configured: no data graph has been set up for this source yet. Your consultant sets them up.',
  NOT_PICKED: 'Pick the data graph this source is pulled through.',
  PARENT_NO_KEY: 'This data graph has no key table, and the subset’s row selection starts there.',
  NO_KEY: 'This data graph has no key, and a subset with several sources needs one on every source.',
  NO_KEY_NAME: 'The parent’s data graph has no key name, and it names the subset’s shared key.',
};

/**
 * The subset's sources: which connections move as a unit, which one is the parent, and the data
 * graph each is pulled through. The graph carries everything about HOW a source is pulled — the key
 * it is matched on, how far linked tables are followed, its manual links — and is set up by a
 * consultant in the operator console; here the user only picks one. The subset owns the sources
 * directly; there is no source group in between.
 */
const SourcesSection = ({ sources, graphsBySource, onSetGraph, onSetParent, onRemove, onAdd }) => {
  const parent = sources.find((m) => m.parent) || null;
  const parentGraph = parent ? chosenGraph(parent, graphsBySource) : null;
  return (
    <div className='tdm-section' data-testid='tdm-sources'>
      <SectionTitle title='Sources' testId='tdm-sources-help'
        help={`The parent's selection decides what is pulled; every other source follows by the shared key. The subset pattern runs against ${parent ? parent.dataSourceName : 'the parent'} and produces the row set, the key values are gathered from it, and each other connection is pulled where its key holds those values. Each source is pulled through the data graph picked for it: the key it is matched on, how far linked tables are followed, and its manual links.`} />

      {sources.length === 0 && <div className='tdm-empty' data-testid='tdm-no-sources'>No sources yet. Add the connection this subset pulls from.</div>}
      {sources.length > 0 && (
        <div className='tdm-table tdm-table--members'>
          <div className='tdm-table__head'><span /><span>Connection</span><span>Engine</span><span>Data graph</span><span>Parent</span><span /></div>
          {sources.map((m) => {
            const options = (graphsBySource && graphsBySource[String(m.dataSourceId)]) || [];
            const g = chosenGraph(m, graphsBySource);
            const problem = sourceGraphProblem(m, graphsBySource, sources.length);
            return (
              <div className='tdm-table__row' key={m.dataSourceId} data-testid={`tdm-member-${m.dataSourceId}`}>
                <span className={`tdm-badge${m.parent ? ' tdm-badge--parent' : ''}`}>{engineBadge(m.engine)}</span>
                <span className='dg-name'>{m.dataSourceName}</span>
                <span className='tdm-muted'>{engineLabel(m)}</span>
                <span>
                  {options.length > 0 && (
                    <Form.Control as='select' size='sm' value={m.dataGraphId != null ? String(m.dataGraphId) : ''}
                      onChange={(e) => onSetGraph(m.dataSourceId, e.target.value ? Number(e.target.value) : null)}
                      data-testid={`tdm-source-graph-${m.dataSourceId}`} aria-label={`Data graph for ${m.dataSourceName}`}>
                      {!g && <option value=''>Choose a data graph…</option>}
                      {options.map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                    </Form.Control>
                  )}
                  {g && (
                    <div className='tdm-muted' data-testid={`tdm-source-graph-info-${m.dataSourceId}`}>
                      Key: {graphKeyLabel(g) || 'none'} · Max child hops: {graphHopsLabel(g)}
                    </div>
                  )}
                  {g && graphTestLine(g) && (
                    <div className={graphTestLine(g).tone === 'warn' ? 'tdm-warn' : 'tdm-muted'} data-testid={`tdm-source-graph-test-${m.dataSourceId}`}>
                      {graphTestLine(g).text}{' '}
                      <Link to={graphPath(m.dataSourceId, g.id)} data-testid={`tdm-source-graph-open-${m.dataSourceId}`}>Open the data graph</Link>
                    </div>
                  )}
                  {problem && (
                    <div className={problem === 'NOT_PICKED' ? 'tdm-muted' : 'tdm-error'}
                      data-testid={problem === 'NOT_CONFIGURED' ? `tdm-source-graph-none-${m.dataSourceId}`
                        : problem === 'NOT_PICKED' ? `tdm-source-graph-unpicked-${m.dataSourceId}`
                          : `tdm-source-graph-nokey-${m.dataSourceId}`}>
                      {PROBLEM_TEXT[problem]}
                    </div>
                  )}
                </span>
                <span><Form.Check type='radio' name='tdm-parent' id={`parent-${m.dataSourceId}`} label='Parent' checked={Boolean(m.parent)} onChange={() => onSetParent(m.dataSourceId)} data-testid={`tdm-parent-${m.dataSourceId}`} /></span>
                <span className='dg-actions'>
                  <button type='button' className='tdm-link' data-testid={`tdm-remove-source-${m.dataSourceId}`} onClick={() => onRemove(m.dataSourceId)}>Remove</button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div className='tdm-row-end mt-2'>
        <Button size='sm' variant='outline-primary' type='button' onClick={onAdd} data-testid='tdm-add-source'>Add source</Button>
      </div>
      {sources.length > 1 && parentGraph && parentGraph.keyName && (
        <div className='tdm-note mt-2' data-testid='tdm-shared-key'>
          Shared key: <strong>{parentGraph.keyName}</strong>. Each source&apos;s data graph says where it keeps it.
        </div>
      )}
    </div>
  );
};

export default SourcesSection;
